import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { supabase } from '../lib/supabase';
import {
  Plus,
  Save,
  Eye,
  ArrowLeft,
  Trash2,
  GripVertical,
  Type,
  AlignLeft,
  List,
  CheckSquare,
  Calendar,
  Hash,
  Mail,
  Phone,
  File,
  ChevronDown,
  ChevronUp,
} from 'lucide-react';

interface Section {
  id: string;
  title: string;
  description: string;
  order_index: number;
  fields: Field[];
}

interface Field {
  id: string;
  field_type: string;
  label: string;
  description: string;
  required: boolean;
  options: string[];
  order_index: number;
}

interface Template {
  id: string;
  name: string;
  status: string;
  current_version: number;
}

const fieldTypes = [
  { type: 'text', label: 'Short Text', icon: Type },
  { type: 'textarea', label: 'Long Text', icon: AlignLeft },
  { type: 'select', label: 'Dropdown', icon: List },
  { type: 'radio', label: 'Radio Buttons', icon: CheckSquare },
  { type: 'checkbox', label: 'Checkboxes', icon: CheckSquare },
  { type: 'date', label: 'Date', icon: Calendar },
  { type: 'number', label: 'Number', icon: Hash },
  { type: 'email', label: 'Email', icon: Mail },
  { type: 'phone', label: 'Phone', icon: Phone },
];

export default function AssessmentFormEditor() {
  const { templateId } = useParams();
  const navigate = useNavigate();
  const [template, setTemplate] = useState<Template | null>(null);
  const [versionId, setVersionId] = useState<string>('');
  const [sections, setSections] = useState<Section[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [expandedSection, setExpandedSection] = useState<string | null>(null);

  useEffect(() => {
    fetchTemplate();
  }, [templateId]);

  const fetchTemplate = async () => {
    try {
      const { data: templateData, error: templateError } = await supabase
        .from('assessment_templates')
        .select('*')
        .eq('id', templateId)
        .maybeSingle();

      if (templateError) throw templateError;
      setTemplate(templateData);

      const { data: versionData, error: versionError } = await supabase
        .from('assessment_versions')
        .select('*')
        .eq('template_id', templateId)
        .order('version_number', { ascending: false })
        .limit(1)
        .maybeSingle();

      if (versionError) throw versionError;

      if (versionData) {
        setVersionId(versionData.id);
        await fetchSections(versionData.id);
      }
    } catch (error) {
      console.error('Error fetching template:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchSections = async (vId: string) => {
    try {
      const { data: sectionsData, error: sectionsError } = await supabase
        .from('assessment_sections')
        .select('*')
        .eq('version_id', vId)
        .order('order_index');

      if (sectionsError) throw sectionsError;

      const sectionsWithFields = await Promise.all(
        (sectionsData || []).map(async (section) => {
          const { data: fieldsData, error: fieldsError } = await supabase
            .from('assessment_fields')
            .select('*')
            .eq('section_id', section.id)
            .order('order_index');

          if (fieldsError) throw fieldsError;

          return {
            ...section,
            fields: fieldsData || [],
          };
        })
      );

      setSections(sectionsWithFields);
    } catch (error) {
      console.error('Error fetching sections:', error);
    }
  };

  const addSection = async () => {
    try {
      const { data, error } = await supabase
        .from('assessment_sections')
        .insert({
          version_id: versionId,
          title: 'New Section',
          description: '',
          order_index: sections.length,
        })
        .select()
        .single();

      if (error) throw error;
      setSections([...sections, { ...data, fields: [] }]);
    } catch (error) {
      console.error('Error adding section:', error);
    }
  };

  const updateSection = async (sectionId: string, updates: Partial<Section>) => {
    try {
      const { error } = await supabase
        .from('assessment_sections')
        .update(updates)
        .eq('id', sectionId);

      if (error) throw error;

      setSections(
        sections.map((s) => (s.id === sectionId ? { ...s, ...updates } : s))
      );
    } catch (error) {
      console.error('Error updating section:', error);
    }
  };

  const deleteSection = async (sectionId: string) => {
    if (!confirm('Delete this section and all its fields?')) return;

    try {
      const { error } = await supabase
        .from('assessment_sections')
        .delete()
        .eq('id', sectionId);

      if (error) throw error;
      setSections(sections.filter((s) => s.id !== sectionId));
    } catch (error) {
      console.error('Error deleting section:', error);
    }
  };

  const addField = async (sectionId: string, fieldType: string) => {
    const section = sections.find((s) => s.id === sectionId);
    if (!section) return;

    try {
      const { data, error } = await supabase
        .from('assessment_fields')
        .insert({
          section_id: sectionId,
          field_type: fieldType,
          label: 'New Field',
          description: '',
          required: false,
          options: [],
          order_index: section.fields.length,
        })
        .select()
        .single();

      if (error) throw error;

      setSections(
        sections.map((s) =>
          s.id === sectionId ? { ...s, fields: [...s.fields, data] } : s
        )
      );
    } catch (error) {
      console.error('Error adding field:', error);
    }
  };

  const updateField = async (
    sectionId: string,
    fieldId: string,
    updates: Partial<Field>
  ) => {
    try {
      const { error } = await supabase
        .from('assessment_fields')
        .update(updates)
        .eq('id', fieldId);

      if (error) throw error;

      setSections(
        sections.map((s) =>
          s.id === sectionId
            ? {
                ...s,
                fields: s.fields.map((f) =>
                  f.id === fieldId ? { ...f, ...updates } : f
                ),
              }
            : s
        )
      );
    } catch (error) {
      console.error('Error updating field:', error);
    }
  };

  const deleteField = async (sectionId: string, fieldId: string) => {
    try {
      const { error } = await supabase
        .from('assessment_fields')
        .delete()
        .eq('id', fieldId);

      if (error) throw error;

      setSections(
        sections.map((s) =>
          s.id === sectionId
            ? { ...s, fields: s.fields.filter((f) => f.id !== fieldId) }
            : s
        )
      );
    } catch (error) {
      console.error('Error deleting field:', error);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    await new Promise((resolve) => setTimeout(resolve, 500));
    setSaving(false);
  };

  const getFieldIcon = (fieldType: string) => {
    const field = fieldTypes.find((f) => f.type === fieldType);
    return field?.icon || Type;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading editor...</p>
        </div>
      </div>
    );
  }

  if (!template) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="text-center">
          <p className="text-gray-600">Template not found</p>
          <button
            onClick={() => navigate('/assessments/admin')}
            className="mt-4 text-purple-600 hover:text-purple-700"
          >
            Back to Assessments
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="h-full flex flex-col">
      <div className="bg-white border-b border-gray-200 px-6 py-4 flex items-center justify-between">
        <div className="flex items-center space-x-4">
          <button
            onClick={() => navigate('/assessments/admin')}
            className="p-2 text-gray-600 hover:bg-gray-100 rounded-lg"
          >
            <ArrowLeft className="w-5 h-5" />
          </button>
          <div>
            <h1 className="text-xl font-bold text-gray-900">{template.name}</h1>
            <p className="text-sm text-gray-600">
              Version {template.current_version} • {template.status}
            </p>
          </div>
        </div>
        <div className="flex items-center gap-3">
          <button
            onClick={handleSave}
            disabled={saving}
            className="px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors font-medium flex items-center disabled:opacity-50"
          >
            <Save className="w-4 h-4 mr-2" />
            {saving ? 'Saving...' : 'Save'}
          </button>
        </div>
      </div>

      <div className="flex-1 overflow-hidden flex">
        <div className="w-64 bg-gray-50 border-r border-gray-200 p-4 overflow-y-auto">
          <h3 className="text-sm font-semibold text-gray-900 mb-3">
            Form Components
          </h3>
          <div className="space-y-2">
            {fieldTypes.map((fieldType) => {
              const Icon = fieldType.icon;
              return (
                <div
                  key={fieldType.type}
                  className="p-3 bg-white border border-gray-200 rounded-lg cursor-move hover:border-purple-500 hover:shadow-sm transition-all"
                  draggable
                >
                  <div className="flex items-center">
                    <Icon className="w-4 h-4 text-gray-600 mr-2" />
                    <span className="text-sm font-medium text-gray-700">
                      {fieldType.label}
                    </span>
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        <div className="flex-1 overflow-y-auto p-6">
          <div className="max-w-4xl mx-auto">
            <div className="mb-6">
              <button
                onClick={addSection}
                className="w-full p-4 border-2 border-dashed border-gray-300 rounded-lg text-gray-600 hover:border-purple-500 hover:text-purple-600 transition-colors flex items-center justify-center"
              >
                <Plus className="w-5 h-5 mr-2" />
                Add Section
              </button>
            </div>

            <div className="space-y-6">
              {sections.map((section, sectionIndex) => (
                <div
                  key={section.id}
                  className="bg-white border border-gray-200 rounded-lg overflow-hidden"
                >
                  <div className="bg-gray-50 px-4 py-3 border-b border-gray-200 flex items-center justify-between">
                    <div className="flex items-center space-x-3 flex-1">
                      <GripVertical className="w-5 h-5 text-gray-400 cursor-move" />
                      <input
                        type="text"
                        value={section.title}
                        onChange={(e) =>
                          updateSection(section.id, { title: e.target.value })
                        }
                        className="flex-1 px-2 py-1 text-lg font-semibold text-gray-900 bg-transparent border-0 focus:outline-none focus:ring-2 focus:ring-purple-500 rounded"
                      />
                    </div>
                    <div className="flex items-center gap-2">
                      <button
                        onClick={() =>
                          setExpandedSection(
                            expandedSection === section.id ? null : section.id
                          )
                        }
                        className="p-1 text-gray-600 hover:bg-gray-100 rounded"
                      >
                        {expandedSection === section.id ? (
                          <ChevronUp className="w-5 h-5" />
                        ) : (
                          <ChevronDown className="w-5 h-5" />
                        )}
                      </button>
                      <button
                        onClick={() => deleteSection(section.id)}
                        className="p-1 text-red-600 hover:bg-red-50 rounded"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </div>

                  {(expandedSection === section.id || expandedSection === null) && (
                    <div className="p-4">
                      <input
                        type="text"
                        value={section.description}
                        onChange={(e) =>
                          updateSection(section.id, {
                            description: e.target.value,
                          })
                        }
                        placeholder="Section description (optional)"
                        className="w-full px-3 py-2 text-sm text-gray-600 border border-gray-300 rounded-lg mb-4 focus:outline-none focus:ring-2 focus:ring-purple-500"
                      />

                      <div className="space-y-3">
                        {section.fields.map((field, fieldIndex) => {
                          const FieldIcon = getFieldIcon(field.field_type);
                          return (
                            <div
                              key={field.id}
                              className="p-4 border border-gray-200 rounded-lg hover:border-purple-500 transition-colors"
                            >
                              <div className="flex items-start gap-3">
                                <GripVertical className="w-5 h-5 text-gray-400 cursor-move mt-2" />
                                <FieldIcon className="w-5 h-5 text-gray-600 mt-2" />
                                <div className="flex-1">
                                  <input
                                    type="text"
                                    value={field.label}
                                    onChange={(e) =>
                                      updateField(section.id, field.id, {
                                        label: e.target.value,
                                      })
                                    }
                                    className="w-full px-2 py-1 font-medium text-gray-900 border-0 focus:outline-none focus:ring-2 focus:ring-purple-500 rounded"
                                  />
                                  <input
                                    type="text"
                                    value={field.description}
                                    onChange={(e) =>
                                      updateField(section.id, field.id, {
                                        description: e.target.value,
                                      })
                                    }
                                    placeholder="Field description"
                                    className="w-full px-2 py-1 text-sm text-gray-600 border-0 focus:outline-none focus:ring-2 focus:ring-purple-500 rounded"
                                  />

                                  {(field.field_type === 'select' ||
                                    field.field_type === 'radio' ||
                                    field.field_type === 'checkbox') && (
                                    <div className="mt-2">
                                      <label className="text-xs text-gray-500">
                                        Options (comma separated):
                                      </label>
                                      <input
                                        type="text"
                                        value={field.options.join(', ')}
                                        onChange={(e) =>
                                          updateField(section.id, field.id, {
                                            options: e.target.value
                                              .split(',')
                                              .map((o) => o.trim())
                                              .filter((o) => o),
                                          })
                                        }
                                        placeholder="Option 1, Option 2, Option 3"
                                        className="w-full mt-1 px-2 py-1 text-sm border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-purple-500"
                                      />
                                    </div>
                                  )}

                                  <div className="mt-2 flex items-center">
                                    <input
                                      type="checkbox"
                                      id={`required-${field.id}`}
                                      checked={field.required}
                                      onChange={(e) =>
                                        updateField(section.id, field.id, {
                                          required: e.target.checked,
                                        })
                                      }
                                      className="w-4 h-4 text-purple-600 border-gray-300 rounded focus:ring-purple-500"
                                    />
                                    <label
                                      htmlFor={`required-${field.id}`}
                                      className="ml-2 text-sm text-gray-700"
                                    >
                                      Required field
                                    </label>
                                  </div>
                                </div>
                                <button
                                  onClick={() =>
                                    deleteField(section.id, field.id)
                                  }
                                  className="p-1 text-red-600 hover:bg-red-50 rounded"
                                >
                                  <Trash2 className="w-4 h-4" />
                                </button>
                              </div>
                            </div>
                          );
                        })}
                      </div>

                      <div className="mt-4 flex flex-wrap gap-2">
                        {fieldTypes.map((fieldType) => (
                          <button
                            key={fieldType.type}
                            onClick={() => addField(section.id, fieldType.type)}
                            className="px-3 py-1 text-xs bg-gray-100 text-gray-700 rounded hover:bg-purple-100 hover:text-purple-700 transition-colors"
                          >
                            + {fieldType.label}
                          </button>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
